import React, { useState, useEffect, useCallback } from 'react';
import { Mail, CheckCircle, Linkedin, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { newsletterAPI, linkedinPostsAPI } from '../services/api';
import { sanitizeEmbed } from '../lib/sanitizeEmbed';

const Newsletter = () => {
  const [activeTab, setActiveTab] = useState('newsletter');
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  const loadLinkedInPosts = useCallback(async () => {
    try {
      const res = await linkedinPostsAPI.getAll();
      setLinkedinPosts(res.data || []);
    } catch (error) {
      console.error('Failed to load LinkedIn posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadLinkedInPosts();
  }, [loadLinkedInPosts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    try {
      await newsletterAPI.subscribe(email);
      setIsSubscribed(true);
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Stay Connected</h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
            Subscribe to our newsletter and follow our LinkedIn updates for the latest in renewable energy
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => setActiveTab('newsletter')}
              data-testid="tab-newsletter"
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'newsletter'
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Mail className="w-5 h-5" />
              Newsletter
            </button>
            <button
              onClick={() => setActiveTab('linkedin')}
              data-testid="tab-linkedin"
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'linkedin'
                  ? 'bg-[#0077B5] text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Linkedin className="w-5 h-5" />
              LinkedIn Posts
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Tab Content */}
      {activeTab === 'newsletter' && (
        <>
          {/* Subscription Form */}
          <section className="py-20 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-2xl p-10 shadow-xl">
                <div className="text-center mb-10">
                  <div className="inline-flex w-20 h-20 bg-emerald-600 rounded-full items-center justify-center mb-6">
                    <Mail className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Subscribe to Our Newsletter</h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Get exclusive insights, regulatory updates, and expert tips delivered to your inbox
                  </p>
                </div>

                {!isSubscribed ? (
                  <form onSubmit={handleSubmit} className="max-w-md mx-auto" data-testid="newsletter-form">
                    <div className="mb-6">
                      <Label htmlFor="newsletter-email" className="text-lg mb-2 block">
                        Your Email Address
                      </Label>
                      <Input
                        id="newsletter-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="text-lg py-6"
                        required
                        data-testid="newsletter-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 text-lg"
                      data-testid="newsletter-submit-btn"
                    >
                      Subscribe Now
                    </Button>
                    <p className="text-sm text-gray-500 mt-4 text-center">
                      We respect your privacy. Unsubscribe anytime.
                    </p>
                  </form>
                ) : (
                  <div className="text-center" data-testid="newsletter-success">
                    <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">You're Subscribed!</h3>
                    <p className="text-gray-600">Check your inbox for a confirmation email.</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What You'll Get</h2>
                <p className="text-lg text-gray-600">Valuable insights delivered to your inbox</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Regulatory Updates</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Stay informed about the latest policy changes and regulatory requirements in renewable energy
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Project Success Stories</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Learn from real-world case studies and discover best practices from successful projects
                  </p>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Insights</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Access exclusive analysis and commentary from our team of renewable energy experts
                  </p>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* LinkedIn Tab Content */}
      {activeTab === 'linkedin' && (
        <section className="py-16 bg-gray-50 min-h-[60vh]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex w-16 h-16 bg-[#0077B5] rounded-full items-center justify-center mb-4">
                <Linkedin className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Our LinkedIn Updates</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Follow our latest posts, industry insights, and company news from LinkedIn
              </p>
            </div>

            {loadingPosts ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0077B5] mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading posts...</p>
              </div>
            ) : linkedinPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="linkedin-posts-grid">
                {linkedinPosts.map((post, index) => (
                  <div 
                    key={post._id} 
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow flex flex-col"
                    style={{ height: '450px' }}
                    data-testid={`linkedin-post-card-${index}`}
                  >
                    {post.title && (
                      <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0">
                        <h3 className="font-medium text-gray-900 text-sm truncate">{post.title}</h3>
                      </div>
                    )}
                    <div 
                      className="linkedin-embed-container p-2 flex-1 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmbed(post.embedCode) }}
                      style={{ 
                        maxWidth: '100%',
                        minHeight: '0'
                      }}
                    />
                    {post.linkedinUrl && (
                      <div className="px-4 py-3 border-t bg-gray-50 flex-shrink-0">
                        <a
                          href={post.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 text-[#0077B5] hover:text-[#005885] font-medium text-sm transition-colors"
                          data-testid={`view-on-linkedin-${index}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                          View on LinkedIn
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl shadow-lg" data-testid="no-linkedin-posts">
                <Linkedin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Posts Yet</h3>
                <p className="text-gray-500 mb-6">We're working on sharing our LinkedIn updates here soon.</p>
                <a
                  href="https://www.linkedin.com/company/solarian-astra"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#0077B5] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#005885] transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                  Follow us on LinkedIn
                </a>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Newsletter;
